import React, { useEffect, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { useInsert } from '../../hooks/useInsert';
import { RibbonGroup, RibbonSeparator, LargeButton, SmallButton, Stack, MenuButton, PopButton, MenuItemRow, TableGridPicker } from './RibbonPrimitives';
import { ShapesGallery } from '../editor/ShapesGallery';
import { DropdownMenuItem, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { DATE_TIME_FORMATS } from '../../lib/textUtils';
import { getRecentSymbols } from '../../lib/symbols';
import { getBusinessInfo } from '../../lib/projectApi';
import { BuildingBlockDropdown } from './BuildingBlocksGallery';
import { StaticPage } from '../editor/ObjectRenderer';
import { WordArtInsertMenu } from './WordArtGallery';
import {
  FilePlus2, Files, Table as TableIcon, Image as ImageIcon, Shapes, ImagePlus, LayoutTemplate, CalendarDays, Frame, Megaphone, Type, Building2, Omega, Clock, Link2, Bookmark, PanelTop, PanelBottom, Hash, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const BLOCK_CATEGORIES = [
  { id: 'Page Parts', icon: LayoutTemplate, label: 'Page\nParts' },
  { id: 'Calendars', icon: CalendarDays, label: 'Calendars' },
  { id: 'Borders & Accents', icon: Frame, label: 'Borders &\nAccents' },
  { id: 'Advertisements', icon: Megaphone, label: 'Advertise-\nments' },
];

export const InsertTab = () => {
  const { state, dispatch, openDialog } = useEditor();
  const ins = useInsert();
  const [biz, setBiz] = useState(state.businessInfo);
  const [openBlocks, setOpenBlocks] = useState(null);
  useEffect(() => { if (!biz) getBusinessInfo().then(setBiz).catch(() => {}); }, [biz]);
  const recentSymbols = getRecentSymbols();

  const insertBiz = (field) => {
    const v = biz?.[field];
    if (!v) { toast.info('That field is empty. Edit Business Information first.'); openDialog('business-info'); return; }
    if (field === 'logo') ins.insertImageSrc(v);
    else ins.insertText(v);
  };

  return (
    <>
      <RibbonGroup label="Pages" testId="ribbon-group-pages">
        <MenuButton icon={FilePlus2} label="Page" large tip="Insert a page" testId="ribbon-command-insert-page">
          <DropdownMenuItem onClick={() => { dispatch({ type: 'ADD_PAGE', payload: { index: state.currentPageIndex + 1 } }); toast.success('Blank page inserted'); }} className="text-[13px]" data-testid="ribbon-menu-insert-blank-page">Insert Blank Page</DropdownMenuItem>
          <DropdownMenuItem onClick={() => { dispatch({ type: 'ADD_PAGE', payload: { index: state.currentPageIndex + 1, duplicate: true } }); toast.success('Page duplicated'); }} className="text-[13px]" data-testid="ribbon-menu-insert-duplicate-page">Insert Duplicate Page</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => openDialog('insert-page')} className="text-[13px]" data-testid="ribbon-menu-insert-page-dialog">Insert Page...</DropdownMenuItem>
        </MenuButton>
        <LargeButton icon={Files} label={'Catalog\nPages'} onClick={() => toast.info('Catalog merge is not available yet.')} tip="Catalog pages (merge with a data source)" testId="ribbon-command-catalog-pages" />
      </RibbonGroup>
      <RibbonSeparator />

      <RibbonGroup label="Tables" testId="ribbon-group-tables">
        <PopButton icon={TableIcon} label="Table" large tip="Insert a table" testId="ribbon-command-insert-table" width="w-auto">
          <TableGridPicker onPick={(r, c) => ins.insertTable(r, c)} />
          <div className="border-t border-border mt-1 pt-1"><MenuItemRow label="Insert Table..." onClick={() => openDialog('insert-table')} testId="ribbon-menu-insert-table-dialog-2" /></div>
        </PopButton>
      </RibbonGroup>
      <RibbonSeparator />

      <RibbonGroup label="Illustrations" testId="ribbon-group-illustrations">
        <LargeButton icon={ImageIcon} label="Pictures" onClick={ins.insertPictureFromFile} tip="Insert a picture from your computer" testId="ribbon-command-insert-picture-button" />
        <LargeButton icon={ImagePlus} label={'Online\nPictures'} onClick={() => ins.openOnlinePictures('insert')} tip="Search Unsplash and insert a photo" testId="ribbon-command-online-pictures" />
        <ShapesGallery onPick={(id) => ins.startShapeTool(id)}>
          <LargeButton icon={Shapes} label="Shapes" chevron tip="Insert ready-made shapes" testId="ribbon-command-insert-shapes" />
        </ShapesGallery>
        <LargeButton icon={Frame} label={'Picture\nPlaceholder'} onClick={ins.startPlaceholderTool} active={state.activeTool === 'picture-placeholder'} tip="Draw a placeholder frame for a picture" testId="ribbon-command-picture-placeholder" />
      </RibbonGroup>
      <RibbonSeparator />

      <RibbonGroup label="Building Blocks" testId="ribbon-group-building-blocks">
        {BLOCK_CATEGORIES.map((c) => (
          <PopButton key={c.id} icon={c.icon} label={c.label} large tip={`${c.id} building blocks`} testId={`ribbon-command-blocks-${c.id.toLowerCase().replace(/[^a-z]+/g, '-')}`} width="w-auto" open={openBlocks === c.id} onOpenChange={(o) => setOpenBlocks(o ? c.id : null)}>
            {openBlocks === c.id && <BuildingBlockDropdown category={c.id} onClose={() => setOpenBlocks(null)} />}
          </PopButton>
        ))}
      </RibbonGroup>
      <RibbonSeparator />

      <RibbonGroup label="Text" testId="ribbon-group-text">
        <LargeButton icon={Type} label={'Draw\nText Box'} onClick={ins.startTextBoxTool} active={state.activeTool === 'text'} tip="Draw Text Box" testId="ribbon-command-insert-text-box" />
        <MenuButton icon={Building2} label={'Business\nInformation'} large tip="Insert your business information" testId="ribbon-command-business-info">
          {[['name', 'Individual name'], ['title', 'Job position or title'], ['organization', 'Organization name'], ['address', 'Address'], ['phone', 'Phone'], ['email', 'Email'], ['website', 'Website'], ['tagline', 'Tagline or motto'], ['logo', 'Logo']].map(([k, l]) => (
            <DropdownMenuItem key={k} onClick={() => insertBiz(k)} className="text-[13px]" data-testid={`ribbon-menu-biz-${k}`}>
              <span className="flex-1">{l}</span>{biz?.[k] && k !== 'logo' && <span className="text-[11px] text-muted-foreground ml-3 truncate max-w-[140px]">{biz[k]}</span>}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => openDialog('business-info')} className="text-[13px]" data-testid="ribbon-menu-biz-edit">Edit Business Information...</DropdownMenuItem>
        </MenuButton>
        <WordArtInsertMenu onInsert={(st, transform) => ins.insertWordArt(st, transform)} />
        <Stack>
          <MenuButton icon={Omega} label="Symbol" tip="Insert a symbol or special character" testId="ribbon-command-symbol">
            {recentSymbols.length > 0 && (
              <div className="grid grid-cols-8 gap-px p-1">{recentSymbols.slice(0, 16).map((ch) => <button key={ch} type="button" onClick={() => ins.insertText(ch)} className="h-7 w-7 rounded-sm border border-border hover:bg-primary/15 text-[15px]" data-testid={`ribbon-symbol-recent-${ch.codePointAt(0)}`}>{ch}</button>)}</div>
            )}
            {recentSymbols.length === 0 && <div className="grid grid-cols-8 gap-px p-1">{['©', '®', '™', '€', '£', '¥', '§', '¶', '•', '…', '–', '—', '≠', '≤', '≥', '∞'].map((ch) => <button key={ch} type="button" onClick={() => ins.insertText(ch)} className="h-7 w-7 rounded-sm border border-border hover:bg-primary/15 text-[15px]" data-testid={`ribbon-symbol-quick-${ch.codePointAt(0)}`}>{ch}</button>)}</div>}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => openDialog('symbol')} className="text-[13px]" data-testid="ribbon-menu-more-symbols">More Symbols...</DropdownMenuItem>
          </MenuButton>
          <MenuButton icon={Clock} label="Date & Time" tip="Insert the current date or time" testId="ribbon-command-date-time">
            {DATE_TIME_FORMATS.map((f) => <DropdownMenuItem key={f.id} onClick={() => ins.insertText(f.label(new Date()))} className="text-[13px]" data-testid={`ribbon-menu-date-${f.id}`}>{f.label(new Date())}</DropdownMenuItem>)}
          </MenuButton>
          <SmallButton icon={FileText} label="Insert File..." onClick={() => ins.insertFile()} tip="Insert the text of a document (.docx, .doc, .rtf, .odt, .txt, .md, .html) at the insertion point, into the selected text box, or into a new text box" testId="ribbon-command-insert-file" />
        </Stack>
      </RibbonGroup>
      <RibbonSeparator />

      <RibbonGroup label="Links" testId="ribbon-group-links">
        <LargeButton icon={Link2} label="Hyperlink" onClick={() => { if (state.selectedObjectIds.length !== 1) { toast.info('Select one object to add a hyperlink.'); return; } openDialog('hyperlink', { objectId: state.selectedObjectIds[0] }); }} tip="Add a hyperlink (Ctrl+K)" testId="ribbon-command-hyperlink" />
        <LargeButton icon={Bookmark} label="Bookmark" onClick={() => toast.info('Bookmarks are coming soon.')} tip="Bookmark" testId="ribbon-command-bookmark" disabled />
      </RibbonGroup>
      <RibbonSeparator />

      <RibbonGroup label="Header & Footer" testId="ribbon-group-header-footer">
        <LargeButton icon={PanelTop} label="Header" onClick={() => ins.insertHeaderFooter('header')} tip="Insert a header text box" testId="ribbon-command-header" />
        <LargeButton icon={PanelBottom} label="Footer" onClick={() => ins.insertHeaderFooter('footer')} tip="Insert a footer text box" testId="ribbon-command-footer" />
        <MenuButton icon={Hash} label={'Page\nNumber'} large tip="Insert a page number field" testId="ribbon-command-page-number">
          <DropdownMenuItem onClick={() => ins.insertPageNumber('header')} className="text-[13px]" data-testid="ribbon-menu-page-number-top">Top of Page</DropdownMenuItem>
          <DropdownMenuItem onClick={() => ins.insertPageNumber('footer')} className="text-[13px]" data-testid="ribbon-menu-page-number-bottom">Bottom of Page</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => ins.insertText('#')} className="text-[13px]">Current Text Position</DropdownMenuItem>
        </MenuButton>
      </RibbonGroup>
    </>
  );
};
